// Typ: FindCircumradiusFromSidesTask
// Temat: Okręgi w trójkącie → Okrąg opisany (trzy boki)
// Wzory:  P = sqrt(p(p-a)(p-b)(p-c)),  p=(a+b+c)/2,  R = abc/(4P)
// Równoważnie: R = abc / sqrt((a+b+c)(-a+b+c)(a-b+c)(a+b-c))

import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";

// ===== utils (te same co w inradius z 3 boków) =====
const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// wyciąga kwadraty spod pierwiastka:  n = outside^2 * inside (inside square-free)
function simplifySqrtInt(n) {
  if (n <= 0) return { outside: 0, inside: 1 };
  let out = 1, inside = n;
  for (let i = 2; i * i <= inside; i++) {
    const sq = i * i;
    while (inside % sq === 0) {
      out *= i;
      inside /= sq;
    }
  }
  return { outside: out, inside };
}

// LaTeX: (coeff * sqrt(root)) / den  z automatycznym skróceniem;
// gdy root==1 → zwykły ułamek/całkowita/mieszana
function formatCoeffWithRoot(coeff, den, root) {
  const d = gcd(Math.abs(coeff), Math.abs(den));
  let num = coeff / d;
  let denS = den / d;

  if (!root || root === 1) {
    const sign = num < 0 ? "-" : "";
    const absNum = Math.abs(num);
    if (denS === 1) return `${num}`;
    if (absNum < denS) return `${sign}\\dfrac{${absNum}}{${denS}}`;
    const whole = Math.floor(absNum / denS);
    const rem = absNum % denS;
    if (rem === 0) return `${sign}${whole}`;
    return `${sign}${whole}\\dfrac{${rem}}{${denS}}`;
  }

  const sign = num < 0 ? "-" : "";
  const nabs = Math.abs(num);
  if (nabs === 1 && denS === 1) return `${sign}\\sqrt{${root}}`;
  if (denS === 1) return `${sign}${nabs}\\sqrt{${root}}`;
  if (nabs === 1) return `${sign}\\dfrac{\\sqrt{${root}}}{${denS}}`;
  return `${sign}\\dfrac{${nabs}\\sqrt{${root}}}{${denS}}`;
}

// ===== generator =====
// Na górze pliku możesz dodać (albo zostawić w funkcji):
const MAX_RADICAND = 100;      // górny limit tego, co zostaje pod √ po uproszczeniu
const SMALL_SIDE_CAP = 16;     // „małe” boki akceptujemy bez warunku na √

export function generateTask() {
  // pomocnicze
  const okTriangle = (a, b, c) => (a + b > c && a + c > b && b + c > a);
  const insideOf = (a, b, c) => {
    const prod = (a + b + c) * (-a + b + c) * (a - b + c) * (a + b - c);
    return simplifySqrtInt(prod).inside; // square-free część – to właśnie „radicand”
  };

  // 1) losowanie z priorytetem: małe boki albo mały √
  for (let i = 0; i < 1200; i++) {
    const a = rnd(6, 24), b = rnd(6, 24), c = rnd(6, 24);
    if (!okTriangle(a, b, c)) continue;

    const maxSide = Math.max(a, b, c);
    const inside = insideOf(a, b, c);
    if (maxSide <= SMALL_SIDE_CAP || inside <= MAX_RADICAND) {
      return { type: "FindCircumradiusFromSidesTask", data: { a, b, c } };
    }
  }

  // 2) „Ładne” seedy + skalowanie (też pilnujemy, by inside ≤ MAX_RADICAND)
  const NICE_BASE = [
    [13,14,15], [7,10,12], [8,9,13], [6,7,11], [9,10,15],
    [10,13,15], [11,12,17], // kilka z małym radicandem
    [3,4,5], [5,12,13], [8,15,17], [7,24,25] // prostokątne (inside = 1)
  ];
  for (let t = 0; t < 200; t++) {
    const [x, y, z] = NICE_BASE[Math.floor(Math.random() * NICE_BASE.length)];
    const kMax = Math.floor(24 / Math.max(x, y, z));
    if (kMax < 1) continue;
    const k = Math.floor(Math.random() * kMax) + 1;
    const a = x * k, b = y * k, c = z * k;
    if (!okTriangle(a, b, c)) continue;
    if (insideOf(a, b, c) <= MAX_RADICAND) {
      return { type: "FindCircumradiusFromSidesTask", data: { a, b, c } };
    }
  }

  // 3) awaryjnie: pewny, „ładny” przypadek
  return { type: "FindCircumradiusFromSidesTask", data: { a: 8, b: 15, c: 17 } };
}


// ===== render task =====
export function renderTask({ data }) {
  const { a, b, c } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      W trójkącie <InlineMath math={"ABC"} /> dane są boki
      <InlineMath math={`\\;a=${a}`} />, <InlineMath math={`\\;b=${b}`} /> i{" "}
      <InlineMath math={`\\;c=${c}`} />. Oblicz promień okręgu opisanego{" "}
      <span style={{ whiteSpace: "nowrap" }}>
        <InlineMath math={"R"} />.
      </span>
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={"R"}
        placeholder="np. (15√6)/14, √3, 3.46"
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { a, b, c } = data;

  // Heron
  const p = (a + b + c) / 2;
  const P = Math.sqrt(p * (p - a) * (p - b) * (p - c));

  const expected = (a * b * c) / (4 * P); // R = abc/(4P)

  // 1) parse z RootFractionInput
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // 2) fallback: liczba dziesiętna (obsługa przecinka)
  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;
  // akceptuj dziesiętne ±0.01
  return Math.abs(parsed - expected) <= 0.01;
}

// ===== answer =====
export function renderAnswer({ data }) {
  const { a, b, c } = data;

  // prod = (a+b+c)(-a+b+c)(a-b+c)(a+b-c);  P = sqrt(prod)/4  ⇒  R = abc / sqrt(prod)
  const prod =
    (a + b + c) * (-a + b + c) * (a - b + c) * (a + b - c);

  const { outside, inside } = simplifySqrtInt(prod);
  // R = abc / (outside * sqrt(inside)) = (abc * sqrt(inside)) / (outside * inside)
  const latex = formatCoeffWithRoot(a * b * c, outside * inside, inside);

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`R = ${latex}`} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { a, b, c } = data;

  // półobwód i różnice
  const p = (a + b + c) / 2;
  const pa = p - a, pb = p - b, pc = p - c;

  // prod do Herona: P = sqrt(prod)/4
  const prod = (a + b + c) * (-a + b + c) * (a - b + c) * (a + b - c);

  // wyciągamy kwadraty spod pierwiastka
  const { outside, inside } = simplifySqrtInt(prod);

  // zapisy LaTeX jak w zadaniu o polu
  const P_sqrt       = `\\sqrt{${p}\\cdot ${pa}\\cdot ${pb}\\cdot ${pc}}`;
  const P_prod       = `\\dfrac{\\sqrt{${prod}}}{4}`;
  const P_simplified = formatCoeffWithRoot(outside, 4, inside);

  // w Kroku 4 korzystamy z P_simplified: 4P = outside*sqrt(inside)
  const fourP_latex = inside === 1 ? `${outside}` : `${outside}\\,\\sqrt{${inside}}`;

  // pokazujemy pełne podstawienie i redukcję 4·(…/4)
  const R_useP = inside === 1
    ? `R = \\dfrac{${a}\\cdot ${b}\\cdot ${c}}{4\\cdot \\left(\\dfrac{${outside}}{4}\\right)}`
    : `R = \\dfrac{${a}\\cdot ${b}\\cdot ${c}}{4\\cdot \\left(\\dfrac{${outside}\\,\\sqrt{${inside}}}{4}\\right)}`;

  // po zredukowaniu 4: R = abc / (outside * sqrt(inside))
  const R_afterReduce = `R = \\dfrac{${a}\\cdot ${b}\\cdot ${c}}{${fourP_latex}}`;

  // wynik końcowy w ładnej postaci (przeniesienie sqrt do licznika = „formatCoeffWithRoot”)
  const R_final = formatCoeffWithRoot(a * b * c, outside * inside, inside);

  return (
    <div style={{ textAlign: 'left', lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wzór Herona i związek z promieniem opisanym:</div>
      <BlockMath math={'P = \\sqrt{p\\,(p-a)\\,(p-b)\\,(p-c)},\\quad p=\\dfrac{a+b+c}{2}'} />
      <BlockMath math={'R = \\dfrac{abc}{4P}'} />

      <div><strong>Krok 2.</strong> Liczymy półobwód i różnice:</div>
      <BlockMath math={`p = \\dfrac{${a}+${b}+${c}}{2} = ${p}`} />
      <BlockMath math={`p-a = ${p}-${a} = ${pa}`} />
      <BlockMath math={`p-b = ${p}-${b} = ${pb}`} />
      <BlockMath math={`p-c = ${p}-${c} = ${pc}`} />

      <div><strong>Krok 3.</strong> Podstawiamy do Herona i upraszczamy pole:</div>
      <BlockMath math={`P = ${P_sqrt}`} />
      <BlockMath math={`P = ${P_prod}`} />
      <BlockMath math={`P = ${P_simplified}`} />

      <div><strong>Krok 4.</strong> Promień okręgu opisanego:</div>
      <BlockMath math={'R = \\dfrac{abc}{4P}'} />
      <BlockMath math={R_useP} />
      <BlockMath math={R_afterReduce} />
      <BlockMath math={`R = ${R_final}`} />
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, "R")} />
    </div>
  );
}

// ===== default export (dla spójności z agregatorem named/default) =====
export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
